import { defineStore } from "pinia";
import api from "../api";
import { onServerEvent } from "../eventStream";
import type { RouteMapItem } from "../types/routes";
import { useAuthStore } from "./auth";

// Вне state: учёт подписки и таймера дебаунса, реактивность не нужна.
let subscribedToUpdates = false;
let refreshTimer: number | null = null;

// Отдельный (не routes.ts) стор: "мои маршруты" - другая семантика
// инвалидации (не публичный каталог, требует авторизации, список меняется
// только собственными действиями пользователя в редакторе, а не чужими SSE
// "route" событиями каталога). Используется точкой входа "открыть мой
// маршрут" в редакторе (useRouteEditor.ts) и вкладкой "Мои" раздела
// "Мои маршруты" в профиле (ProfileView.vue).
export const useMyRoutesStore = defineStore("myRoutes", {
  state: () => ({
    items: [] as RouteMapItem[],
    loading: false,
    loaded: false,
  }),
  actions: {
    async fetch(force = false) {
      this._subscribeToUpdates();
      if (this.loaded && !force) return;
      this.loading = true;
      try {
        const res = await api.get<RouteMapItem[]>("/routes/mine");
        this.items = res.data || [];
      } catch {
        this.items = [];
      } finally {
        this.loading = false;
        this.loaded = true;
      }
    },
    // Фоновое обновление без loading/скелетонов - для SSE-сигналов: новые
    // маршруты и изменившиеся лайки/просмотры/сохранения появляются в
    // «Мои маршруты» без перезагрузки страницы.
    async refresh() {
      if (!useAuthStore().isAuthenticated) return;
      try {
        const res = await api.get<RouteMapItem[]>("/routes/mine");
        this.items = res.data || [];
        this.loaded = true;
      } catch {
        // фоновое обновление - при ошибке остаётся текущее состояние
      }
    },
    // Серия событий (например, несколько просмотров подряд) схлопывается в
    // один запрос.
    _scheduleRefresh() {
      if (refreshTimer != null) window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => {
        refreshTimer = null;
        void this.refresh();
      }, 300);
    },
    _subscribeToUpdates() {
      if (subscribedToUpdates) return;
      subscribedToUpdates = true;
      // Любое изменение маршрутов (в т.ч. сохранение нового в редакторе) -
      // перечитываем список, если он уже загружался.
      onServerEvent("route", () => {
        if (this.loaded) this._scheduleRefresh();
      });
      // Лайки/просмотры/сохранения: интересуют только свои маршруты (сигнал
      // несёт лишь route_id - сверяем с уже загруженным списком).
      onServerEvent("route-engagement", (data) => {
        const routeId = (data as { route_id?: number } | null)?.route_id;
        if (this.loaded && this.items.some((item) => item.id === routeId)) this._scheduleRefresh();
      });
    },
    // Переключатель приватности (замочек на карточке). Возвращает true при
    // успехе; на ошибке состояние карточки не меняется.
    async setVisibility(routeId: number, isPublic: boolean): Promise<boolean> {
      try {
        const res = await api.patch<RouteMapItem>(`/routes/${routeId}/visibility`, { is_public: isPublic });
        const item = this.items.find((r) => r.id === routeId);
        if (item) item.is_public = res.data.is_public;
        return true;
      } catch {
        return false;
      }
    },
    // Удаление своего маршрута (DELETE /routes/{id}). true - удалён и убран из
    // списка; на ошибке список не меняется.
    async remove(routeId: number): Promise<boolean> {
      try {
        await api.delete(`/routes/${routeId}`);
        this.items = this.items.filter((r) => r.id !== routeId);
        return true;
      } catch {
        return false;
      }
    },
    invalidate() {
      this.loaded = false;
    },
  },
});
